import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Database, CaretLeft, Calculator, Bank, Factory, UsersThree, EnvelopeSimple, DownloadSimple } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const WEBAPPS = [
  { key: "contabilita", name: "Contabilità", color: "#38BDF8", icon: Calculator },
  { key: "tesoreria", name: "Tesoreria", color: "#E9A23B", icon: Bank },
  { key: "produzione", name: "Produzione", color: "#9F1239", icon: Factory },
  { key: "risorice", name: "Risorice", color: "#E05E3F", icon: UsersThree },
  { key: "posta", name: "Posta", color: "#F97316", icon: EnvelopeSimple },
];

export default function MarketingDatabasePage() {
  const [status, setStatus] = useState({});
  const [importing, setImporting] = useState(null);

  const loadStatus = () => api.get("/admin/marketing/database/status").then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { loadStatus(); }, []);

  const handleImport = async (area) => {
    setImporting(area);
    try {
      const { data } = await api.post(`/admin/marketing/database/${area}/import`);
      toast.success(data.message, { duration: 9000 });
      loadStatus();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail), { duration: 9000 });
    } finally {
      setImporting(null);
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="marketing-database-page">
      <div className="mb-10 animate-fade-up">
        <Link to="/marketing/email" data-testid="back-to-email" className="inline-flex items-center gap-1 text-xs font-semibold text-white/50 hover:text-white transition-colors mb-4">
          <CaretLeft size={13} /> Email
        </Link>
        <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2 flex items-center gap-1.5">
          <Database size={13} className="text-sky-400" /> Email — Database
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">Database</h1>
        <p className="text-sm text-slate-400 mt-3 max-w-2xl">
          Importa i contatti dalle WebApp collegate: finiscono nella lista "Webapp — Nome", pronta per le newsletter. Ripeti l'import quando vuoi: aggiunge solo i contatti nuovi.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="database-webapps-grid">
        {WEBAPPS.map((w, i) => {
          const Icon = w.icon;
          const st = status[w.key];
          return (
            <div key={w.key} data-testid={`database-webapp-${w.key}`}
              className="p-6 rounded-lg bg-surface border border-surface-border animate-fade-up"
              style={{ animationDelay: `${60 + i * 60}ms` }}>
              <div className="flex items-center gap-4">
                <span className="w-12 h-12 rounded-lg border flex items-center justify-center shrink-0"
                  style={{ borderColor: `${w.color}55`, background: `${w.color}12` }}>
                  <Icon size={24} weight="duotone" style={{ color: w.color }} />
                </span>
                <div className="min-w-0">
                  <h3 className="font-heading text-lg font-semibold tracking-tight">{w.name}</h3>
                  <p className="text-[11px] text-white/40">{w.key}.areapaq.it</p>
                </div>
              </div>
              {st ? (
                <p className="mt-4 text-[11px] text-emerald-400" data-testid={`import-status-${w.key}`}>
                  Ultimo import: {new Date(st.at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })} — {st.total} contatti in "{st.list_name}"
                </p>
              ) : (
                <p className="mt-4 text-[11px] text-white/35" data-testid={`import-status-${w.key}`}>Nessun import ancora eseguito</p>
              )}
              <Button data-testid={`import-button-${w.key}`} onClick={() => handleImport(w.key)} disabled={importing === w.key}
                className="mt-3 w-full h-10 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-xs disabled:opacity-40">
                <DownloadSimple size={14} className="mr-1.5" /> {importing === w.key ? "Import in corso..." : "Importa contatti"}
              </Button>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-white/35 mt-6">
        La webapp deve esporre l'endpoint <code className="text-sky-300">GET /api/paq/contacts</code> — scarica la <a href="/GUIDA_ENDPOINT_CONTATTI.md" download className="text-sky-300 underline underline-offset-2" data-testid="guida-endpoint-link">guida pronta per gli sviluppatori</a> (esempi FastAPI, Node.js e PHP) o la <a href="/SPEC_DATI_ANALISI_MARKETING.md" download className="text-sky-300 underline underline-offset-2">specifica dati completa</a>.
      </p>
    </main>
  );
}
